import { type JSX } from 'react';
import { useRouter } from 'next/router';
import { abbreviate } from '@chainflip/utils/string';
import { flip$ } from '@/shared/assets/tokens';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import Pill, { type PillProps } from '@/shared/components/flip-ui-kit/Pill';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { OperatorLogo } from '@/shared/components/operators/Logos';
import WalletActionButton from '@/shared/components/WalletActionButton';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { type GetOperatorsRewardsQuery } from '@/shared/graphql/generated/explorer/graphql';
import useDelegateFlip from '@/shared/hooks/useDelegateFlip';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import { compareDelegationApy, type DelegationApy } from '@/shared/utils/apy';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import { formatBps, formatPct } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';
import { getOperatorsRewardsQuery } from '../../../graphql/explorer/operatorQueries';
import useOperatorsAndAuction, {
  type OperatorSummary,
} from '../../../hooks/useOperatorsAndAuction';

const SplitText = ({
  upper,
  lower,
  pillProps,
}: {
  upper: string;
  lower: string;
  pillProps?: PillProps;
}) => (
  <div className="flex flex-col whitespace-nowrap text-14">
    <span className="flex items-center gap-x-1 text-cf-white">
      {upper} {pillProps && <Pill {...pillProps} />}
    </span>
    <span className="text-cf-light-2">{lower}</span>
  </div>
);

const Apy = ({ apy }: { apy: DelegationApy }) => {
  if (apy.type === 'apyBp') return <>{formatBps(apy.apyBp)}</>;
  if (apy.type === 'apyPct') return <>{formatPct(apy.apyPct)}</>;
  if (apy.type === 'n/a') return '-';
  return null;
};

const Row = ({
  operator,
  rewardsData,
}: {
  operator: OperatorSummary;
  rewardsData: GetOperatorsRewardsQuery | undefined;
}) => {
  const router = useRouter();
  const { nextEpoch } = useEvmScAccount();
  const { price } = useTokenPrice(flip$);

  const totalDelegatedFlip = operator.nextEpoch.totalDelegation;

  const totalDelegatedFlipUsd =
    price !== undefined ? totalDelegatedFlip.toNumber() * price : undefined;

  const rewards = rewardsData?.allAccounts?.nodes.find(
    (account) => account.idSs58 === operator.idSs58,
  )?.operator?.operatorEpoch.aggregates?.sum;

  const totalRewards = TokenAmount.fromAsset(rewards?.amount, 'Flip');
  const totalRewardsUsd = rewards?.valueUsd;

  const { mutate: delegate, isPending } = useDelegateFlip();

  return (
    <ClickableTableRow key={operator.idSs58} href={`/operators/${operator.idSs58}`}>
      <td className="flex items-center gap-x-2">
        <OperatorLogo size="large" idSs58={operator.idSs58} />
        <SplitText
          upper={operator.alias ?? abbreviate(operator.idSs58)}
          pillProps={{
            color: 'neutral',
            text: formatBps(operator.currentEpoch.commissionBps),
            tooltipContent: 'Operator Commission',
          }}
          lower={`${operator.allValidators.totalCount} nodes, ${operator.currentEpoch.authorities} authorities`}
        />
      </td>
      <td className="text-20 text-cf-white">
        <Apy apy={operator.weightedDelegatorApy} />
      </td>
      <td className="text-20 text-cf-white">
        <Apy apy={operator.currentEpoch.delegatorApy} />
      </td>
      <td className="text-20 text-cf-white">
        <Apy apy={operator.nextEpoch.delegatorApy} />
      </td>
      <td>
        <SplitText
          upper={`${totalDelegatedFlip.toCapped()} ${FLIP_SYMBOL}`}
          lower={formatUsdValue(totalDelegatedFlipUsd) ?? '$-'}
        />
      </td>
      <td>
        <SplitText
          upper={`${totalRewards?.toCapped()} ${FLIP_SYMBOL}`}
          lower={formatUsdValue(totalRewardsUsd) ?? '$-'}
        />
      </td>
      <td>
        {nextEpoch?.operatorIdSs58 !== operator.idSs58 && (
          <WalletActionButton
            chainId={chainflipAssetMap.Flip.chain.id}
            requireConnection
            type="primary-standard"
            className="whitespace-nowrap"
            size="small"
            loading={isPending && 'iconOnly'}
            disabled={!operator.isDelegationAllowed || 'ifLoading'}
            noPropagate
            onClick={() => {
              if (nextEpoch?.operatorIdSs58) {
                delegate({ operator: operator.idSs58, amount: TokenAmount.ZERO });
              } else {
                router.push(`/delegate?operator=${operator.idSs58}`);
              }
            }}
          >
            {nextEpoch?.operatorIdSs58 ? 'Switch Operator' : `Delegate ${FLIP_SYMBOL}`}
          </WalletActionButton>
        )}
      </td>
    </ClickableTableRow>
  );
};

export function OperatorsTable(): JSX.Element {
  const { operators = [], isLoading } = useOperatorsAndAuction({
    refetchInterval: TABLE_POLL_INTERVAL,
  });

  const { isLoading: isLoadingRewards, data: rewardsData } = useGqlQuery(getOperatorsRewardsQuery, {
    refetchInterval: TABLE_POLL_INTERVAL,
    variables: { operators: operators.map((o) => o.idSs58) },
    enabled: operators.length > 0,
    context: { clientName: 'processor' },
  });

  return (
    <TableV2
      className="font-aeonikMedium"
      columns={[
        'Operator',
        {
          name: 'Historical APY',
          tooltip:
            "A 10-epoch (~30-day) weighted average APY for Delegators. This value is based on the Operator's performance over the past 10 epochs and may not reflect future returns.",
        },
        {
          name: 'Current APY',
          tooltip:
            "The Delegators' APY for the current epoch. If the Operator's Validators successfully author all blocks in their authorship slots, the Delegators can expect to earn this APY. Actual returns may vary.",
        },
        {
          name: 'Upcoming APY',
          tooltip:
            "The Delegators' projected APY for the upcoming epoch. If the auction were to resolve now, the Delegators would have this APY in the next epoch. Actual returns may vary.",
        },
        {
          name: 'Delegated',
          tooltip: 'The total amount of FLIP delegated to this Operator by all Delegators.',
        },
        {
          name: 'Rewards',
          tooltip:
            'The rewards earned by this Operator, distributed between the Operator and its Delegators.',
        },
        '',
      ]}
      rows={operators
        .toSorted((a, b) =>
          compareDelegationApy(b.nextEpoch.delegatorApy, a.nextEpoch.delegatorApy),
        )
        .map((operator) => (
          <Row key={operator.idSs58} operator={operator} rewardsData={rewardsData} />
        ))}
      isLoading={isLoading || isLoadingRewards}
    />
  );
}
